import { useState } from 'react';
import { z } from 'zod';
import { Link } from 'react-router';

// images
import LogoImg from '@assets/logo.png';

// UI
import Button from '@components/button';
import AuthInput from '@components/auth-input';
import BackButton from '@components/back-button';

// styles
import css from './styles.module.css';

interface FormInputs {
  email: string;
}

const validationSchema = z.object({
  email: z.string().email({ message: 'Invalid email format' }),
});

const URL = '/api/auth/forgot-password';

function ForgotPassword() {
  const [formData, setFormData] = useState<FormInputs>({
    email: '',
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof FormInputs, string>>
  >({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      validationSchema.parse(formData);

      const response = await fetch(URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      console.log('RESULT: ', data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Partial<Record<keyof FormInputs, string>> = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof FormInputs;
          fieldErrors[field] = err.message;
        });
        setErrors(fieldErrors);
      }
    }
  };

  return (
    <main className={css.forgotPasswordRoot}>
      <div className={css.content}>
        <img src={LogoImg} alt="logo" className={css.logo} />

        <BackButton className={css.backButton} />

        <h1 className={css.title}>Forgot password?</h1>
        <p className={css.subtitle}>
          Enter your email address, and we'll send you a password recovery link
        </p>
        <form className={css.form} onSubmit={handleSubmit}>
          <AuthInput
            name="email"
            value={formData.email}
            onChange={handleChange}
            type="email"
            placeholder="Email"
            error={errors.email}
            className={css.input}
          />
          <Button size="large" type="submit" className={css.submitButton}>
            Send reset link
          </Button>
        </form>

        <p className={css.linkText}>
          Don't you have an account?{' '}
          <Link to="/sign-up" className={css.loginInLink}>
            Sign up
          </Link>
        </p>
      </div>
    </main>
  );
}

export default ForgotPassword;
